import { FunctionComponent, useEffect, useMemo, useState } from 'react';

import { Alert } from '@mui/material';
import { BrightnessAutoOutlined, DarkModeOutlined, LightModeOutlined, PaletteOutlined } from '@mui/icons-material';

import { SelectionTileGroup } from '@dw/components/core';
import type { ISelectionTileOption } from '@dw/components/core';
import { useAppSettings, useAuth } from '@dw/hooks';
import { useUpdateDanceSchoolAppSettingsMutation } from '@dw/hooks/api/DanceSchoolApi';
import { DANCE_SCHOOL_APP_APPEARANCE } from '@dw/models/danceSchool';
import { USER_ROLE } from '@dw/models/danceSchoolUser';

import { SettingsSection } from '../shared';
import { isWideLogo } from '../illustrations';

import { AppearancePreview } from './AppearancePreview';

const APPEARANCE_OPTIONS: ISelectionTileOption<DANCE_SCHOOL_APP_APPEARANCE>[] = [
  {
    value: DANCE_SCHOOL_APP_APPEARANCE.DANCER_CHOICE,
    label: 'Each dancer decides',
    caption: 'Light, dark, or follow their phone',
    icon: <BrightnessAutoOutlined color='primary' />
  },
  {
    value: DANCE_SCHOOL_APP_APPEARANCE.LIGHT,
    label: 'Always light',
    caption: 'Warm paper, dark type',
    icon: <LightModeOutlined color='primary' />
  },
  {
    value: DANCE_SCHOOL_APP_APPEARANCE.DARK,
    label: 'Always dark',
    caption: 'Dark ground, pale type',
    icon: <DarkModeOutlined color='primary' />
  }
];

/**
 * Whether the school pins the app's appearance, or leaves it to the dancer.
 *
 * Saved the moment a tile is picked rather than behind a Save button — this is one choice with an
 * immediate picture of its result, and a form that needs submitting would put a step between the
 * decision and the evidence for it.
 *
 * The tile state is held locally and re-synced from the school so the picture moves under the
 * pointer instead of after the round trip. The mutation writes the school back into the query cache
 * the Auth context reads, so the two converge without a refetch.
 */
export const AppearanceSettings: FunctionComponent = () => {

  const { danceSchool, userHasRole } = useAuth();
  const { appSettings } = useAppSettings();

  const canEdit = userHasRole(USER_ROLE.OWNER, USER_ROLE.ADMIN);

  const savedAppearance = danceSchool?.appAppearance ?? DANCE_SCHOOL_APP_APPEARANCE.DANCER_CHOICE;
  const [appearance, setAppearance] = useState<DANCE_SCHOOL_APP_APPEARANCE>(savedAppearance);

  const { mutate: updateAppSettings, isPending: isSaving } = useUpdateDanceSchoolAppSettingsMutation();

  /* The app shows the app logo where there is one and falls back to the invoice logo, so the preview does too. */
  const logoKey = danceSchool?.appLogoKey ?? danceSchool?.logoKey;
  const logoUrl = logoKey ? `${appSettings.blobBaseUrl}/${logoKey}` : undefined;

  const appLogoIsWide = useMemo(
    () => isWideLogo(
      danceSchool?.appLogoKey && danceSchool?.appLogoWidth && danceSchool?.appLogoHeight
        ? { width: danceSchool.appLogoWidth, height: danceSchool.appLogoHeight }
        : undefined
    ),
    [danceSchool]
  );

  useEffect(() => setAppearance(savedAppearance), [savedAppearance]);

  const handleChange = (value: DANCE_SCHOOL_APP_APPEARANCE) => {
    if (!canEdit || value === appearance) {
      return;
    }

    setAppearance(value);
    updateAppSettings({ appAppearance: value });
  };

  return (
    <SettingsSection
      icon={<PaletteOutlined color='primary' />}
      title='Appearance'
      caption='Which ground the app is painted on for your dancers.'
      illustration={(
        <AppearancePreview
          appearance={appearance}
          schoolName={danceSchool?.name ?? 'Your studio'}
          imageUrl={logoUrl}
          isWide={appLogoIsWide}
        />
      )}
      note={appearance === DANCE_SCHOOL_APP_APPEARANCE.DANCER_CHOICE
        ? 'Dancers choose in the app under Account, and can follow their phone. Pin a theme if your logo or your notice colours only work on one of them.'
        : 'Pinned. The appearance control in the app is hidden for your dancers, so everyone sees your school the same way.'}
    >
      <SelectionTileGroup<DANCE_SCHOOL_APP_APPEARANCE>
        label='How the app looks'
        options={APPEARANCE_OPTIONS}
        value={appearance}
        onChange={handleChange}
        disabled={!canEdit || isSaving}
      />

      {!canEdit && (
        <Alert severity='info'>Only an owner or admin can change how the app looks.</Alert>
      )}
    </SettingsSection>
  );
};
